<template>
  <div
    class="fixed top-header left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue"
  >
    <HeaderShadow></HeaderShadow>
    <div class="p-4">
      <form @submit.prevent="addLocalUser">
        <div class="flex pb-4">
          <div class="mr-2 w-1/3">
            <router-link to="/local-admin/local-users">
              <ButtonSecondary
                type="button"
                :name="$t('buttons.cancel')"
                :disabled="loading"
              ></ButtonSecondary>
            </router-link>
          </div>
          <div class="ml-2 w-2/3">
            <Button
              type="submit"
              :name="$t('buttons.addVerify')"
              :disabled="
                !validateEmail(localUser.email) ||
                !required(localUser.name) ||
                !required(localUser.phone) ||
                !validatePhoneNumber(localUser.countryCode, localUser.phone) ||
                loading
              "
              :loading="loading"
            ></Button>
          </div>
        </div>

        <div
          class="bg-nucleus-white transition-all"
          :class="{ 'opacity-50': loading }"
        >
          <div class="flex items-center px-4 py-6 border-b">
            <div
              class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-user"
            ></div>
            <div class="text-2xl capitalize">
              {{ $t("global.defaults.newLocal") }}
            </div>
          </div>
          <div class="px-4 py-6 overflow-y-auto scroll-box">
            <Checkbox
              id="admin"
              v-model:value="localUser.role"
              :name="$t('inputs.adminRole.label')"
            ></Checkbox>
            <Input
              id="name"
              v-model:value="localUser.name"
              input-type="text"
              :label="$t('inputs.name.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.name.placeholder')"
              required
            />
            <Input
              id="email"
              v-model:value="localUser.email"
              :update:value="validateEmail(localUser.email)"
              input-type="text"
              :label="$t('inputs.email.label')"
              :max-length="inputMaxLengths.email"
              :placeholder="$t('inputs.email.placeholder')"
              :error="error.email"
              required
            />
            <div class="flex">
              <div class="mr-2 w-1/4">
                <SelectCountry
                  id="code"
                  v-model:value="localUser.countryCode"
                  :label="$t('inputs.code.label')"
                  :options="countryCodes"
                  :options-common="countryCodesCommon"
                />
              </div>
              <div class="ml-2 w-3/4">
                <Input
                  id="phone"
                  v-model:value="localUser.phone"
                  :update:value="
                    validatePhoneNumber(localUser.countryCode, localUser.phone)
                  "
                  input-type="text"
                  :label="$t('inputs.phone.label')"
                  :max-length="inputMaxLengths.phone"
                  :placeholder="$t('inputs.phone.placeholder')"
                  :error="error.phone"
                  required
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      <ErrorBox
        v-if="errorHeading || errorContent"
        :heading="errorHeading"
        :content="errorContent"
        :fixed="errorFixed"
        @closeError="
          errorHeading = '';
          errorContent = '';
        "
      ></ErrorBox>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import SelectCountry from "@/components/utils/SelectCountry.vue"; // @ is an alias to /src
import CountryCodesJson from "@/store/data/countryCodes.json"; // @ is an alias to /src
import CountryCodesCommonJson from "@/store/data/countryCodesCommon.json"; // @ is an alias to /src
import Checkbox from "@/components/utils/Checkbox.vue"; // @ is an alias to /src
import ErrorBox from "@/components/utils/ErrorBox.vue";

import parsePhoneNumber from "libphonenumber-js";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    Button,
    ButtonSecondary,
    HeaderShadow,
    Input,
    Select,
    SelectCountry,
    Checkbox,
    ErrorBox,
  },
  data() {
    return {
      countryCodes: CountryCodesJson,
      countryCodesCommon: CountryCodesCommonJson,
      localUser: {
        name: "",
        email: "",
        countryCode: CountryCodesCommonJson[0].dial_code,
        phone: "",
        role: false,
        tenantId:
          this.$store.getters.getUserObject.attributes["custom:tenancy"],
      },
      correctNumber: "",
      error: {
        email: "",
        phone: "",
      },
      loading: false,
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  mounted() {
    document.title =
      this.$t("global.defaults.addLocal") +
      " - " +
      this.$t("global.defaults.browserTitle");
  },
  methods: {
    addLocalUser() {
      this.loading = true;

      let role = "STAFF";
      if (this.localUser.role) role = "LOCAL_ADMIN";

      const data = {
        name: this.localUser.name,
        email: this.localUser.email,
        phone: this.correctNumber,
        role: role,
        tenantId: this.localUser.tenantId,
      };

      this.$store
        .dispatch("addLocalUser", data)
        .then((response: Record<string, unknown>) => {
          if (response) {
            this.loading = false;
            this.$store.dispatch("changeUpdate", "localUser");
            this.$router.push({ name: "localUsers" });
            console.log("add local admin", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    validateEmail(email: string) {
      const mailformat =
        // eslint-disable-next-line no-useless-escape
        /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

      if (this.required(email)) {
        if (!email.match(mailformat)) {
          this.error.email = this.$t("inputs.email.errorInvalid");
          return false;
        } else {
          this.error.email = "";
          return true;
        }
      } else {
        return false;
      }
    },
    validatePhoneNumber(code: any, phone: any) {
      const phoneNumber = parsePhoneNumber(
        code + phone.replace(/^0+/, "").replace(/\s/g, "")
      );

      if (this.required(phone)) {
        if (phoneNumber?.isValid()) {
          this.error.phone = "";
          this.correctNumber = phoneNumber.number;
          return true;
        } else {
          this.error.phone = this.$t("inputs.phone.errorInvalid");
          return false;
        }
      } else {
        return false;
      }
    },
    required(input: string, inputName: string) {
      if (input.length > 0) {
        if (inputName) this.error[inputName] = "";
        return true;
      } else {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }
    },
  },
})
export default class LocalUsersAdd extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 278px);
}
</style>
